import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import classesService from '../services/classesService';
import staffService from '../services/staffService';
import schoolsService from '../services/schoolsService';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, Trash, Users, BookOpen, Chalkboard, CircleNotch, X, PencilSimple } from '@phosphor-icons/react';
import './ClassDetailPage.css';

const CLASS_LEVELS = [
  { value: 'kg1', label: 'KG 1' },
  { value: 'kg2', label: 'KG 2' },
  { value: 'primary_1', label: 'Primary 1' },
  { value: 'primary_2', label: 'Primary 2' },
  { value: 'primary_3', label: 'Primary 3' },
  { value: 'primary_4', label: 'Primary 4' },
  { value: 'primary_5', label: 'Primary 5' },
  { value: 'primary_6', label: 'Primary 6' },
  { value: 'jhs_1', label: 'JHS 1' },
  { value: 'jhs_2', label: 'JHS 2' },
  { value: 'jhs_3', label: 'JHS 3' },
];

function ClassDetailPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('subjects'); // 'subjects', 'teachers', 'students'

  // Edit class
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', level: 'primary_1', section: '', capacity: 40, room_number: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  
  // Subject management
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedSubjectTerm, setSelectedSubjectTerm] = useState('');
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [subjectTerms, setSubjectTerms] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [submittingSubject, setSubmittingSubject] = useState(false);
  const [removingSubjectId, setRemovingSubjectId] = useState(null);
  
  // Teacher assignment
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    staff_id: '',
    subject_id: '',
    academic_term_id: '',
    is_class_teacher: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingAssignmentId, setDeletingAssignmentId] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null); // the assignment object being edited, or null when creating

  // Waitlist
  const [waitlist, setWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  const [offeringEntryId, setOfferingEntryId] = useState(null);
  
  // Dialog Modal
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });
  const [pendingAction, setPendingAction] = useState(null);

  // Dialog helpers
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
    setPendingAction(null);
  };

  // Load class details
  const loadClassDetails = useCallback(() => {
    setLoading(true);
    classesService.getClass(classId)
      .then(data => {
        setClassData(data);
      })
      .catch(err => {
        toast.error(err.message || 'Failed to load class details');
        navigate('/classes');
      })
      .finally(() => setLoading(false));
  }, [classId, navigate]);

  // Load dropdown data (subjects, terms, and teachers)
  const loadDropdownData = useCallback(async () => {
    setLoadingDropdowns(true);
    try {
      // Load subjects, terms, and teachers in parallel
      const [subjectsData, termsData, teachersData] = await Promise.all([
        classesService.listAllSubjects(),
        user?.school_id ? schoolsService.listTerms(user.school_id) : [],
        staffService.listTeachers()
      ]);
      
      setSubjects(subjectsData || []);
      setAcademicTerms(termsData || []);
      setTeachers(teachersData || []);
    } catch (err) {
      console.error('Failed to load dropdown data:', err);
      toast.error('Failed to load dropdown data');
      setSubjects([]);
      setAcademicTerms([]);
      setTeachers([]);
    } finally {
      setLoadingDropdowns(false);
    }
  }, [user]);



  // Load available subjects for adding to class
  const loadAvailableSubjects = useCallback(async () => {
    setLoadingSubjects(true);
    try {
      const [allSubjects, termsData] = await Promise.all([
        classesService.listAllSubjects(),
        user?.school_id ? schoolsService.listTerms(user.school_id).catch(() => []) : []
      ]);
      
      const assignedSubjectIds = classData?.class_subjects?.map(cs => cs.subject_id) || [];
      const available = (allSubjects || []).filter(s => !assignedSubjectIds.includes(s.id));
      setAvailableSubjects(available);
      setSubjectTerms(Array.isArray(termsData) ? termsData : []);
      
      // Auto-select first term if available
      if (Array.isArray(termsData) && termsData?.length > 0) {
        setSelectedSubjectTerm(termsData[0].id);
      }
    } catch (err) {
      console.error('Failed to load available subjects:', err);
      toast.error('Failed to load available subjects');
      setAvailableSubjects([]);
      setSubjectTerms([]);
    } finally {
      setLoadingSubjects(false);
    }
  }, [classData, user]);

  // Add subject to class
  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!selectedSubjectId) {
      toast.error('Please select a subject');
      return;
    }
    if (!selectedSubjectTerm) {
      toast.error('Please select an academic term');
      return;
    }

    setSubmittingSubject(true);
    try {
      await classesService.addSubjectToClass(classId, selectedSubjectId, selectedSubjectTerm);
      toast.success('Subject added to class!');
      setSelectedSubjectId('');
      setSelectedSubjectTerm('');
      setShowSubjectModal(false);
      loadClassDetails();
    } catch (error) {
      toast.error(error.message || 'Failed to add subject');
    } finally {
      setSubmittingSubject(false);
    }
  };

  // Remove subject from class
  const handleRemoveSubject = (subjectId, academicTermId) => {
    showDialog({
      title: 'Remove Subject',
      message: 'Are you sure you want to remove this subject from the class?',
      type: 'warning',
      primaryAction: {
        label: 'Remove',
        onClick: async () => {
          closeDialog();
          try {
            setRemovingSubjectId(subjectId);
            await classesService.removeSubjectFromClass(classId, subjectId, academicTermId);
            toast.success('Subject removed successfully!');
            loadClassDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to remove subject');
          } finally {
            setRemovingSubjectId(null);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  // Waitlist
  const loadWaitlist = useCallback(() => {
    setLoadingWaitlist(true);
    classesService.getWaitlist(classId)
      .then(data => setWaitlist(data || []))
      .catch(err => toast.error(err.message || 'Failed to load waitlist'))
      .finally(() => setLoadingWaitlist(false));
  }, [classId]);

  useEffect(() => {
    if (activeTab === 'waitlist') loadWaitlist();
  }, [activeTab, loadWaitlist]);

  const handleOfferSeat = async (entryId) => {
    setOfferingEntryId(entryId);
    try {
      const result = await classesService.offerWaitlistSeat(classId, entryId);
      toast.success(result.message || 'Student enrolled from the waitlist');
      loadWaitlist();
      loadClassDetails();
    } catch (error) {
      toast.error(error.message || 'Failed to offer this seat');
    } finally {
      setOfferingEntryId(null);
    }
  };

  const handleCancelWaitlistEntry = (entryId) => {
    showDialog({
      title: 'Cancel Waitlist Entry',
      message: 'Remove this student from the waitlist?',
      type: 'warning',
      primaryAction: {
        label: 'Cancel Entry',
        onClick: async () => {
          closeDialog();
          try {
            await classesService.cancelWaitlistEntry(classId, entryId);
            toast.success('Waitlist entry cancelled');
            loadWaitlist();
          } catch (error) {
            toast.error(error.message || 'Failed to cancel entry');
          }
        }
      },
      secondaryAction: {
        label: 'Keep',
        onClick: closeDialog
      }
    });
  };

  // Edit class
  const handleOpenEditModal = () => {
    setEditForm({
      name: classData.name || '',
      level: classData.level || 'primary_1',
      section: classData.section || '',
      capacity: classData.capacity || 40,
      room_number: classData.room_number || ''
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => setShowEditModal(false);

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditSubmitting(true);
    try {
      await classesService.updateClass(classId, editForm);
      toast.success('Class updated successfully!');
      setShowEditModal(false);
      loadClassDetails();
    } catch (error) {
      toast.error(error.message || 'Failed to update class');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleToggleActive = () => {
    const activating = !classData.is_active;
    showDialog({
      title: activating ? 'Activate Class' : 'Deactivate Class',
      message: activating
        ? 'This class will become active again and visible in normal class listings.'
        : 'This class will be marked inactive. Existing students and records are kept, but the class will be hidden from active listings.',
      type: 'warning',
      primaryAction: {
        label: activating ? 'Activate' : 'Deactivate',
        onClick: async () => {
          closeDialog();
          setTogglingActive(true);
          try {
            await classesService.updateClass(classId, { is_active: activating });
            toast.success(activating ? 'Class activated' : 'Class deactivated');
            loadClassDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to update class status');
          } finally {
            setTogglingActive(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  useEffect(() => {
    loadClassDetails();
  }, [loadClassDetails]);

  const handleOpenSubjectModal = () => {
    loadAvailableSubjects();
    setShowSubjectModal(true);
  };

  const handleCloseSubjectModal = () => {
    setSelectedSubjectId('');
    setSelectedSubjectTerm('');
    setShowSubjectModal(false);
  };

  const handleOpenAssignmentModal = () => {
    // Ensure user is available before loading
    if (!user) {
      toast.error('User information not loaded. Please try again.');
      return;
    }
    setEditingAssignment(null);
    loadDropdownData();
    setShowAssignmentModal(true);
  };

  const handleOpenEditAssignmentModal = (assignment) => {
    if (!user) {
      toast.error('User information not loaded. Please try again.');
      return;
    }
    setEditingAssignment(assignment);
    setAssignmentForm({
      staff_id: assignment.staff_id,
      subject_id: assignment.subject_id,
      academic_term_id: assignment.academic_term_id,
      is_class_teacher: assignment.is_class_teacher
    });
    loadDropdownData();
    setShowAssignmentModal(true);
  };

  const handleCloseAssignmentModal = () => {
    setAssignmentForm({
      staff_id: '',
      subject_id: '',
      academic_term_id: '',
      is_class_teacher: false
    });
    setEditingAssignment(null);
    setShowAssignmentModal(false);
  };

  const handleAssignmentFormChange = (field, value) => {
    setAssignmentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssignTeacher = async (e) => {
    e.preventDefault();

    if (!assignmentForm.staff_id || !assignmentForm.subject_id || !assignmentForm.academic_term_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingAssignment) {
        await staffService.updateAssignment(editingAssignment.staff_id, editingAssignment.id, {
          staff_id: assignmentForm.staff_id,
          is_class_teacher: assignmentForm.is_class_teacher
        });
        toast.success('Assignment updated successfully!');
      } else {
        await staffService.createAssignment(assignmentForm.staff_id, {
          class_id: classId,
          subject_id: assignmentForm.subject_id,
          academic_term_id: assignmentForm.academic_term_id,
          is_class_teacher: assignmentForm.is_class_teacher
        });
        toast.success('Teacher assigned successfully!');
      }

      handleCloseAssignmentModal();
      loadClassDetails();
    } catch (error) {
      toast.error(error.message || 'Failed to save assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveTeacher = (assignmentId, staffId) => {
    showDialog({
      title: 'Remove Teacher',
      message: 'Are you sure you want to remove this teacher from the class?',
      type: 'warning',
      primaryAction: {
        label: 'Remove',
        onClick: async () => {
          closeDialog();
          try {
            setDeletingAssignmentId(assignmentId);
            await staffService.deleteAssignment(staffId, assignmentId);
            toast.success('Teacher removed successfully!');
            loadClassDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to remove teacher');
          } finally {
            setDeletingAssignmentId(null);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading class details...</div>
      </Layout>
    );
  }

  if (!classData) {
    return (
      <Layout>
        <div className="empty-state">Class not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page class-detail-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-with-back">
            <button
              className="btn-icon"
              onClick={() => navigate('/classes')}
              title="Go back"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>{classData.name}</h1>
              <p>Level: {classData.level?.replace('_', ' ').toUpperCase()}</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={handleOpenEditModal}>
              <PencilSimple size={16} weight="bold" /> Edit Class
            </button>
            <button
              className={`btn ${classData.is_active ? 'btn-danger' : 'btn-primary'}`}
              onClick={handleToggleActive}
              disabled={togglingActive}
            >
              {togglingActive ? 'Updating...' : classData.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </div>

        {/* Class Info */}
        <div className="class-info-grid">
          <div className="info-card">
            <h3>Class Information</h3>
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{classData.name}</span>
              </div>
              <div className="info-row">
                <span className="label">Level:</span>
                <span className="value capitalize">{classData.level?.replace('_', ' ')}</span>
              </div>
              {classData.section && (
                <div className="info-row">
                  <span className="label">Section:</span>
                  <span className="value">{classData.section}</span>
                </div>
              )}
              {classData.room_number && (
                <div className="info-row">
                  <span className="label">Room:</span>
                  <span className="value">{classData.room_number}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">Capacity:</span>
                <span className="value">{classData.capacity}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`badge ${classData.is_active ? 'active' : 'inactive'}`}>
                  {classData.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stat stat-students">
              <div className="stat-icon-wrapper students">
                <Users size={28} className="stat-icon" weight="bold" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{classData.student_count}</div>
                <div className="stat-label">Enrolled Students</div>
              </div>
            </div>
            <div className="stat stat-teachers">
              <div className="stat-icon-wrapper teachers">
                <Chalkboard size={28} className="stat-icon" weight="bold" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{classData.teachers?.length || 0}</div>
                <div className="stat-label">Assigned Teachers</div>
              </div>
            </div>
            <div className="stat stat-subjects">
              <div className="stat-icon-wrapper subjects">
                <BookOpen size={28} className="stat-icon" weight="bold" />
              </div>
              <div className="stat-content">
                <div className="stat-value">{classData.subjects?.length || 0}</div>
                <div className="stat-label">Active Subjects</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'subjects' ? 'active' : ''}`}
            onClick={() => setActiveTab('subjects')}
          >
            <BookOpen size={18} weight={activeTab === 'subjects' ? 'bold' : 'regular'} />
            Subjects
            <span className="tab-badge">{classData.class_subjects?.length || 0}</span>
          </button>
          <button 
            className={`tab-button ${activeTab === 'teachers' ? 'active' : ''}`}
            onClick={() => setActiveTab('teachers')}
          >
            <Chalkboard size={18} weight={activeTab === 'teachers' ? 'bold' : 'regular'} />
            Teachers
            <span className="tab-badge">{classData.teachers?.length || 0}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            <Users size={18} weight={activeTab === 'students' ? 'bold' : 'regular'} />
            Students
            <span className="tab-badge">{classData.student_count}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'waitlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('waitlist')}
          >
            <Users size={18} weight={activeTab === 'waitlist' ? 'bold' : 'regular'} />
            Waitlist
          </button>
        </div>

        {/* Tab Content */}
        <div className="tabs-content">
          {/* Subjects Tab */}
          {activeTab === 'subjects' && (
            <div className="section subjects-section">
              <div className="section-header">
                <h2>Class Subjects</h2>
                <button className="btn btn-primary" onClick={handleOpenSubjectModal}>
                  <Plus size={16} weight="bold" /> Add Subject
                </button>
              </div>

              {classData.class_subjects && classData.class_subjects.length > 0 ? (
                <div className="subjects-grid">
                  {classData.class_subjects.map(cs => (
                    <div key={`${cs.subject_id}-${cs.academic_term_id}`} className="subject-card">
                      <div className="subject-card-header">
                        <div>
                          <h3>{cs.subject_name}</h3>
                          <p className="subject-code">{cs.subject_code}</p>
                        </div>
                      </div>
                      <div className="subject-card-actions">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveSubject(cs.subject_id, cs.academic_term_id)}
                          disabled={removingSubjectId === cs.subject_id}
                          title="Remove subject from class"
                        >
                          <Trash size={14} weight="bold" /> 
                          {removingSubjectId === cs.subject_id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-card">
                  <p>No subjects assigned yet</p>
                  <button className="btn btn-primary" onClick={handleOpenSubjectModal}>
                    Add First Subject
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Teachers Tab */}
          {activeTab === 'teachers' && (
            <div className="section teachers-section">
              <div className="section-header">
                <h2>Assigned Teachers</h2>
                {classData.class_subjects && classData.class_subjects.length > 0 ? (
                  <button className="btn btn-primary" onClick={handleOpenAssignmentModal}>
                    <Plus size={16} weight="bold" /> Assign Teacher
                  </button>
                ) : null}
              </div>
              
              {classData.class_subjects && classData.class_subjects.length === 0 ? (
                <div className="empty-state-card">
                  <p>Add subjects first before assigning teachers</p>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => setActiveTab('subjects')}
                  >
                    Go to Subjects Tab
                  </button>
                </div>
              ) : classData.teachers && classData.teachers.length > 0 ? (
                <div className="teachers-grid">
                  {classData.teachers.map(assignment => (
                    <div key={assignment.id} className="teacher-card">
                      <div className="teacher-card-header">
                        <div>
                          <h3>{assignment.teacher_name}</h3>
                          <p className="subject">{assignment.subject_name}</p>
                        </div>
                        {assignment.is_class_teacher && (
                          <span className="class-teacher-badge">Class Teacher</span>
                        )}
                      </div>
                      <div className="teacher-card-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleOpenEditAssignmentModal(assignment)}
                          title="Edit teacher or class-teacher status"
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveTeacher(assignment.id, assignment.staff_id)}
                          disabled={deletingAssignmentId === assignment.id}
                          title="Remove teacher from class"
                        >
                          <Trash size={14} weight="bold" />
                          {deletingAssignmentId === assignment.id ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-card">
                  <p>No teachers assigned yet</p>
                  <button className="btn btn-primary" onClick={handleOpenAssignmentModal}>
                    Assign First Teacher
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Students Tab */}
          {activeTab === 'students' && (
            <div className="section students-section">
              <div className="section-header">
                <h2>Enrolled Students</h2>
              </div>

              {classData.students && classData.students.length > 0 ? (
                <div className="students-grid">
                  {classData.students.map(student => (
                    <div key={student.id} className="student-card">
                      <div className="student-avatar">
                        {student.photo_url ? (
                          <img src={student.photo_url} alt={student.first_name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {student.first_name[0]}{student.last_name[0]}
                          </div>
                        )}
                      </div>
                      <div className="student-info">
                        <h4>{student.first_name} {student.last_name}</h4>
                        <p className="student-id">{student.student_id}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-card">
                  <p>No students enrolled in this class yet</p>
                </div>
              )}
            </div>
          )}

          {/* Waitlist Tab */}
          {activeTab === 'waitlist' && (
            <div className="section waitlist-section">
              <div className="section-header">
                <h2>Waitlist</h2>
              </div>

              {loadingWaitlist ? (
                <div className="loading">Loading waitlist...</div>
              ) : waitlist.length > 0 ? (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Position</th>
                        <th>Student</th>
                        <th>Requested</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {waitlist.map(entry => (
                        <tr key={entry.id}>
                          <td>{entry.position}</td>
                          <td>{entry.student_name}</td>
                          <td>{new Date(entry.requested_at).toLocaleDateString()}</td>
                          <td className="capitalize">{entry.status}</td>
                          <td>
                            {entry.status === 'waiting' && (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleOfferSeat(entry.id)}
                                  disabled={offeringEntryId === entry.id}
                                >
                                  {offeringEntryId === entry.id ? 'Offering...' : 'Offer Seat'}
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleCancelWaitlistEntry(entry.id)}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state-card">
                  <p>No students on the waitlist for this class</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Assignment Modal */}
        {showAssignmentModal && (
          <div className="modal-overlay" onClick={handleCloseAssignmentModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingAssignment ? 'Edit Teacher Assignment' : 'Assign Teacher to Class'}</h2>
                <button className="modal-close" onClick={handleCloseAssignmentModal}>
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleAssignTeacher} className="modal-form">
                <div className="form-group">
                  <label>Teacher *</label>
                  <select
                    value={assignmentForm.staff_id}
                    onChange={(e) => handleAssignmentFormChange('staff_id', e.target.value)}
                    required
                    disabled={loadingDropdowns}
                  >
                    <option value="">-- Select Teacher --</option>
                    {teachers.map(teacher => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.full_name} ({teacher.department || 'N/A'})
                      </option>
                    ))}
                  </select>
                  {teachers.length === 0 && !loadingDropdowns && (
                    <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
                      No teachers available. Please ensure teachers are created first.
                    </small>
                  )}
                  {editingAssignment && (
                    <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                      Pick a different teacher here to replace who teaches this subject.
                    </small>
                  )}
                </div>

                {editingAssignment ? (
                  <div className="form-group">
                    <label>Subject</label>
                    <p style={{ margin: 0, color: '#6b7280' }}>
                      {editingAssignment.subject_name} — subject and term can't be changed on an
                      existing assignment; remove and re-add instead if that's needed.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="form-group">
                      <label>Subject *</label>
                      <select
                        value={assignmentForm.subject_id}
                        onChange={(e) => handleAssignmentFormChange('subject_id', e.target.value)}
                        required
                        disabled={loadingDropdowns}
                      >
                        <option value="">-- Select Subject --</option>
                        {(classData?.class_subjects || []).map(cs => (
                          <option key={cs.subject_id} value={cs.subject_id}>
                            {cs.subject_name} ({cs.subject_code})
                          </option>
                        ))}
                      </select>
                      {(!classData?.class_subjects || classData.class_subjects.length === 0) && (
                        <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
                          No subjects assigned to this class. Please add subjects first in the Subjects tab.
                        </small>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Academic Term *</label>
                      <select
                        value={assignmentForm.academic_term_id}
                        onChange={(e) => handleAssignmentFormChange('academic_term_id', e.target.value)}
                        required
                        disabled={loadingDropdowns}
                      >
                        <option value="">
                          {loadingDropdowns ? '-- Loading Terms --' : academicTerms.length === 0 ? '-- No Terms Available --' : '-- Select Term --'}
                        </option>
                        {academicTerms.map(term => (
                          <option key={term.id} value={term.id}>
                            {term.academic_year} - {term.term?.charAt(0).toUpperCase() + term.term?.slice(1)} Term
                          </option>
                        ))}
                      </select>
                      {academicTerms.length === 0 && !loadingDropdowns && (
                        <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
                          No academic terms found for your school. Please create one in settings first.
                        </small>
                      )}
                    </div>
                  </>
                )}

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={assignmentForm.is_class_teacher}
                      onChange={(e) => handleAssignmentFormChange('is_class_teacher', e.target.checked)}
                    />
                    Mark as Class Teacher
                  </label>
                </div>

                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleCloseAssignmentModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting || loadingDropdowns}
                  >
                    {isSubmitting
                      ? (editingAssignment ? 'Saving...' : 'Assigning...')
                      : (editingAssignment ? 'Save Changes' : 'Assign Teacher')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Subject Assignment Modal */}
        {showSubjectModal && (
          <div className="modal-overlay" onClick={handleCloseSubjectModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add Subject to Class</h2>
                <button className="modal-close" onClick={handleCloseSubjectModal}>
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleAddSubject} className="modal-form">
                <div className="form-group">
                  <label>Subject *</label>
                  <select
                    value={selectedSubjectId}
                    onChange={(e) => setSelectedSubjectId(e.target.value)}
                    disabled={loadingSubjects || submittingSubject}
                    required
                  >
                    <option value="">-- Select Subject --</option>
                    {availableSubjects.map(subject => (
                      <option key={subject.id} value={subject.id}>
                        {subject.name} ({subject.code})
                      </option>
                    ))}
                  </select>
                  {availableSubjects.length === 0 && !loadingSubjects && (
                    <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
                      All available subjects are already assigned to this class.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Academic Term *</label>
                  <select
                    value={selectedSubjectTerm}
                    onChange={(e) => setSelectedSubjectTerm(e.target.value)}
                    disabled={loadingSubjects || submittingSubject}
                    required
                  >
                    <option value="">-- Select Term --</option>
                    {subjectTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.academic_year} - {term.term?.charAt(0).toUpperCase() + term.term?.slice(1)} Term
                      </option>
                    ))}
                  </select>
                  {subjectTerms.length === 0 && !loadingSubjects && (
                    <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>
                      No academic terms found. Please create one in settings first.
                    </small>
                  )}
                </div>

                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleCloseSubjectModal}
                    disabled={loadingSubjects || submittingSubject}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loadingSubjects || submittingSubject}
                  >
                    {submittingSubject ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Adding...
                      </span>
                    ) : (
                      'Add Subject'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Class Modal */}
        {showEditModal && (
          <div className="modal-overlay" onClick={handleCloseEditModal}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Edit Class</h2>
                <button className="modal-close" onClick={handleCloseEditModal}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="modal-form">
                <div className="form-group">
                  <label>Class Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    disabled={editSubmitting}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Level</label>
                    <select
                      value={editForm.level}
                      onChange={(e) => setEditForm({ ...editForm, level: e.target.value })}
                      disabled={editSubmitting}
                    >
                      {CLASS_LEVELS.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Section</label>
                    <input
                      type="text"
                      value={editForm.section}
                      onChange={(e) => setEditForm({ ...editForm, section: e.target.value })}
                      placeholder="e.g., A, B, C"
                      disabled={editSubmitting}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Capacity</label>
                    <input
                      type="number"
                      value={editForm.capacity}
                      onChange={(e) => setEditForm({ ...editForm, capacity: parseInt(e.target.value) || 0 })}
                      disabled={editSubmitting}
                      min="1"
                      max="100"
                    />
                  </div>
                  <div className="form-group">
                    <label>Room Number</label>
                    <input
                      type="text"
                      value={editForm.room_number}
                      onChange={(e) => setEditForm({ ...editForm, room_number: e.target.value })}
                      placeholder="e.g., Room 101"
                      disabled={editSubmitting}
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseEditModal} disabled={editSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={editSubmitting}>
                    {editSubmitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Dialog Modal */}
        <DialogModal
          isOpen={dialogConfig.isOpen}
          onClose={closeDialog}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type}
          primaryAction={dialogConfig.primaryAction}
          secondaryAction={dialogConfig.secondaryAction}
        />
      </div>
    </Layout>
  );
}

export default ClassDetailPage;
